import { Box, Card, Grid, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import {
  AccountBalanceOutlined,
  DescriptionOutlined,
  EmailOutlined,
  MapOutlined,
  NumbersOutlined,
  PaletteOutlined,
  SupportAgentOutlined,
  VerifiedUserOutlined,
  WhatsApp, PublicOutlined } from '@mui/icons-material';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader';

// Company is deliberately NOT here. The organisation's own profile lives on Administration ›
// Business Profile › Company, where it is both read and edited; a second entry pointing at the same
// record was the thing that sent people between two sections looking for the same fields.
const sections = [
  { label: 'Branding', to: 'branding', icon: <PaletteOutlined /> },
  { label: 'Email (SMTP)', to: 'email', icon: <EmailOutlined /> },
  { label: 'WhatsApp', to: 'whatsapp', icon: <WhatsApp /> },
  { label: 'Bank accounts', to: 'bank-accounts', icon: <AccountBalanceOutlined /> },
  { label: 'Invoice series', to: 'invoice-series', icon: <NumbersOutlined /> },
  { label: 'Quote & invoice templates', to: 'documents', icon: <DescriptionOutlined /> },
  { label: 'Where we operate', to: 'regions', icon: <PublicOutlined /> },
  { label: 'Places & itinerary', to: 'places', icon: <MapOutlined /> },
  // KYC is a full page of its own (Settings › Company › KYC); support access is a section here.
  { label: 'KYC', to: '/settings/kyc', icon: <VerifiedUserOutlined /> },
  { label: 'Support access', to: 'support', icon: <SupportAgentOutlined /> },
];

/**
 * Modern Settings Center — section nav + routed section content. On phones and tablets the nav is a
 * horizontally scrolling strip above the content; from `md` up it is a sticky left rail.
 */
export default function SettingsLayout() {
  // A guided-setup tip (?guide=…) stays with the person as they move between tabs — the channels step spans
  // Email and WhatsApp.
  const guide = new URLSearchParams(useLocation().search).get('guide');
  const withGuide = (to: string) => (guide && !to.startsWith('/') ? `${to}?guide=${encodeURIComponent(guide)}` : to);
  return (
    <Box>
      <PageHeader title="Settings" subtitle="Configure your company." breadcrumbs={[{ label: 'Settings' }]} dense />
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
          <Card sx={{ p: { xs: 0.75, md: 1 }, position: { md: 'sticky' }, top: { md: 16 } }}>
            <List
              disablePadding
              component="nav"
              aria-label="Settings sections"
              sx={{
                display: { xs: 'flex', md: 'block' },
                gap: { xs: 0.5, md: 0 },
                overflowX: { xs: 'auto', md: 'visible' },
                scrollbarWidth: 'thin',
              }}
            >
              {sections.map((s) => (
                <ListItemButton
                  key={s.to}
                  component={NavLink}
                  to={withGuide(s.to)}
                  sx={{
                    borderRadius: 2,
                    mb: { md: 0.25 },
                    py: { xs: 0.75, md: 1 },
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '&.active': {
                      bgcolor: 'primary.main',
                      color: '#fff',
                      '& .MuiListItemIcon-root': { color: '#fff' },
                    },
                    '&.active:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 32, md: 38 }, '& svg': { fontSize: 20 } }}>{s.icon}</ListItemIcon>
                  <ListItemText primary={s.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
                </ListItemButton>
              ))}
            </List>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }} sx={{ minWidth: 0 }}>
          <Outlet />
        </Grid>
      </Grid>
    </Box>
  );
}
